/*
 * Copyright 2026 Sony Corporation
 */

import { Scene } from '@babylonjs/core/scene';
import { SceneSerializer } from '@babylonjs/core/Misc/sceneSerializer';
import { BackgroundMaterial } from '@babylonjs/core/Materials/Background/backgroundMaterial';
import { CubeTexture } from '@babylonjs/core/Materials/Textures/cubeTexture';
import { Texture } from '@babylonjs/core/Materials/Textures/texture';
import { EncodeArrayBufferToBase64 } from '@babylonjs/core/Misc/stringTools';
import { CreateEnvTextureAsync, type CreateEnvTextureOptions } from '@babylonjs/core/Misc/environmentTextureTools';
import type { AbstractEngine } from '@babylonjs/core/Engines/abstractEngine';
import type { BaseTexture } from '@babylonjs/core/Materials/Textures/baseTexture';

import { SCENE_CONTENT_TYPE, type Scene as SceneResource } from '@ips-device-verse/twin-service/schema/scenes';

import { SKYBOX_MATERIAL, createDocScene } from './apply-to-babylon';
import type { ApiClient } from '../services/api-client';
import type { SceneDoc } from './schema';

/** A cube map is only readable back off the GPU once it has finished decoding. */
const whenReady = (texture: BaseTexture): Promise<void> =>
  new Promise((resolve) => {
    const poll = (): void => {
      if (texture.isReady()) {
        resolve();
        return;
      }
      setTimeout(poll, 50);
    };
    poll();
  });

/**
 * The longest side a published map keeps.
 *
 * A model bought for a render often ships 4k maps, which is sixteen times the
 * pixels of a 1k one and, in a `.babylon`, sixteen times the bytes. The Device
 * page looks at a scene; it does not inspect it.
 */
const MAX_TEXTURE_SIZE = 1024;

/** Colour maps only. Enough that a re-encode is invisible at viewport size. */
const JPEG_QUALITY = 0.85;

/**
 * Maps whose channels are numbers rather than colour.
 *
 * A normal map re-encoded as JPEG produces lit surfaces that ripple, and a
 * roughness channel bleeds into metalness, so these stay lossless and are only
 * ever made smaller.
 */
const DATA_MAPS = [
  'bumpTexture',
  'metallicTexture',
  'reflectivityTexture',
  'ambientTexture',
  'microSurfaceTexture',
  'lightmapTexture',
] as const;

/** Babylon keeps the file it decoded here, and it is what the serializer embeds verbatim. */
const sourceBytes = (texture: Texture): Uint8Array | undefined => {
  const buffer = (texture as unknown as { _buffer?: unknown })._buffer;
  return buffer instanceof Uint8Array ? buffer : undefined;
};

const dataMapsOf = (scene: Scene): Set<unknown> => {
  const lossless = new Set<unknown>();
  for (const material of scene.materials) {
    const fields = material as unknown as Record<string, unknown>;
    for (const field of DATA_MAPS) {
      if (fields[field]) {
        lossless.add(fields[field]);
      }
    }
  }
  return lossless;
};

/**
 * Re-encodes one map, smaller.
 *
 * Works from the bytes Babylon decoded rather than from the GPU: reading the
 * texture back would return whatever the driver stored, premultiplied and
 * possibly flipped, and the file on disc is the thing whose orientation the
 * material was authored against.
 */
const shrinkTexture = async (texture: Texture, lossless: boolean): Promise<void> => {
  const bytes = sourceBytes(texture);
  if (!bytes) {
    return;
  }

  const bitmap = await createImageBitmap(new Blob([bytes as BlobPart]));
  try {
    const scale = Math.min(1, MAX_TEXTURE_SIZE / Math.max(bitmap.width, bitmap.height));
    // Already small, and a lossless map at its authored size has nothing to win.
    if (scale === 1 && lossless) {
      return;
    }

    const canvas = document.createElement('canvas');
    canvas.width = Math.max(1, Math.round(bitmap.width * scale));
    canvas.height = Math.max(1, Math.round(bitmap.height * scale));

    const context = canvas.getContext('2d');
    if (!context) {
      return;
    }
    context.drawImage(bitmap, 0, 0, canvas.width, canvas.height);

    const keepAlpha = lossless || texture.hasAlpha;
    const url = canvas.toDataURL(keepAlpha ? 'image/png' : 'image/jpeg', JPEG_QUALITY);

    // PNG of a photograph can be larger than the JPEG it came from, and a map
    // that grew is a map that should have been left alone.
    if (url.length >= (bytes.byteLength * 4) / 3) {
      return;
    }
    texture.updateURL(url, url);
  } finally {
    bitmap.close();
  }
};

/** One at a time: a 4k map is 64 MB of pixels while it is being redrawn. */
const shrinkTextures = async (scene: Scene): Promise<void> => {
  const lossless = dataMapsOf(scene);
  for (const texture of scene.textures) {
    if (texture instanceof Texture) {
      await shrinkTexture(texture, lossless.has(texture));
    }
  }
};

/** Where a coordinate stops carrying shape and starts carrying float noise. */
const VERTEX_DECIMALS = 4;
const VERTEX_SCALE = 10 ** VERTEX_DECIMALS;

/**
 * Unhooks refraction from the scene's own colour buffer.
 *
 * glTF transmission arrives as a subsurface refraction that samples the opaque
 * scene, which Babylon draws into a render target it creates at runtime. The
 * serializer writes that target out as though it were an ordinary texture, and
 * the loader restores something that is neither: a plain `Texture` still
 * flagged `isRenderTarget`, which the scene then calls `_shouldRender()` on
 * every frame. The throw aborts the frame, so a published scene holding one
 * pane of glass renders nothing at all.
 *
 * Dropping the link leaves the material refracting the environment instead,
 * which is what it does when no refraction texture is set.
 */
const dropRuntimeRefractions = (scene: Scene): void => {
  for (const material of scene.materials) {
    const subSurface = (material as unknown as { subSurface?: { refractionTexture: BaseTexture | null } }).subSurface;
    if (subSurface?.refractionTexture?.isRenderTarget) {
      subSurface.refractionTexture = null;
    }
  }
};

/**
 * Rounds the numbers in a serialized scene.
 *
 * `.babylon` is JSON, so every vertex is written out as text: a float32 widened
 * to a double prints as `0.12340000271797180`, and a scene of a quarter of a
 * million vertices pays eighteen characters for four significant ones. Applied
 * to the serialized form rather than to the geometry because rounding a
 * `Float32Array` changes nothing — the shortest text that round-trips the
 * nearest float32 is still eighteen digits long.
 */
const roundNumbers = (value: unknown): void => {
  if (Array.isArray(value)) {
    for (let index = 0; index < value.length; index += 1) {
      const entry: unknown = value[index];
      if (typeof entry === 'number') {
        value[index] = Math.round(entry * VERTEX_SCALE) / VERTEX_SCALE;
      } else {
        roundNumbers(entry);
      }
    }
    return;
  }

  if (value && typeof value === 'object') {
    for (const entry of Object.values(value)) {
      roundNumbers(entry);
    }
  }
};

const toEnvUrl = async (texture: BaseTexture, options?: CreateEnvTextureOptions): Promise<string> => {
  await whenReady(texture);
  const data = await CreateEnvTextureAsync(texture, options);
  return `data:application/octet-stream;base64,${EncodeArrayBufferToBase64(data)}`;
};

/**
 * Re-points the environment at cube maps carried inside the file.
 *
 * `ForceSerializeBuffers` embeds 2D textures but writes a cube map as a URL,
 * and the loader refuses to fetch a second one (scene-switch.md §2.1). `.env`
 * is the one cube format Babylon can inline, so both cubes are re-encoded into
 * it — each from its own source. The lighting cube is 256 px and prefiltered
 * for rough reflections; a sky encoded from it is a sixteenth of the pixels and
 * visibly blurred.
 */
const embedEnvironment = async (scene: Scene): Promise<void> => {
  const lighting = scene.environmentTexture;
  if (!lighting) {
    return;
  }

  const sky = scene.getMaterialByName(SKYBOX_MATERIAL);
  const backdropSource = sky instanceof BackgroundMaterial ? sky.reflectionTexture : null;

  const lightingUrl = await toEnvUrl(lighting);
  // No irradiance: the sky is looked at, never lit by, and harmonics would only add bytes.
  const backdropUrl = backdropSource ? await toEnvUrl(backdropSource, { disableIrradianceTexture: true }) : undefined;

  const environment = CubeTexture.CreateFromPrefilteredData(lightingUrl, scene, '.env');
  environment.rotationY = (lighting as { rotationY?: number }).rotationY ?? 0;
  scene.environmentTexture = environment;

  if (sky instanceof BackgroundMaterial && backdropUrl) {
    const backdrop = CubeTexture.CreateFromPrefilteredData(backdropUrl, scene, '.env');
    backdrop.coordinatesMode = Texture.SKYBOX_MODE;
    backdrop.rotationY = (backdropSource as { rotationY?: number } | null)?.rotationY ?? 0;
    sky.reflectionTexture = backdrop;
  }
};

/**
 * The build: document in, `.babylon` out (devplan §3).
 *
 * It runs against a **throwaway scene**, not the one on screen, so the output
 * holds the document and nothing else — no editor camera, no editor lights, no
 * gizmo. Serializing the live scene would bake the furniture of the editor into
 * every published file, and nothing about that fails loudly.
 *
 * `ForceSerializeBuffers` is what makes the result self-contained, which is the
 * property the Camera page's one-URL loader depends on (scene-switch.md §2.8).
 * It is also why this is async: the synchronous `Serialize` cannot wait for a
 * texture to be encoded and drops it with a console warning. For the same
 * reason the build waits for the document's own downloads: a model still in
 * flight would be serialized as the empty anchor it is standing in for.
 */
export const buildBabylon = async (engine: AbstractEngine, doc: SceneDoc): Promise<string> => {
  const previous = Texture.ForceSerializeBuffers;
  Texture.ForceSerializeBuffers = true;

  const scene = new Scene(engine);
  const docScene = createDocScene({ scene });

  try {
    docScene.sync(doc);
    await docScene.settled();
    await shrinkTextures(scene);
    dropRuntimeRefractions(scene);
    await embedEnvironment(scene);

    const serialized = await SceneSerializer.SerializeAsync(scene);
    roundNumbers(serialized);
    return JSON.stringify(serialized);
  } finally {
    docScene.dispose();
    scene.dispose();
    Texture.ForceSerializeBuffers = previous;
  }
};

/** Hands the built scene to the browser, for a scene that has nowhere else to go. */
export const downloadBabylon = async (engine: AbstractEngine, doc: SceneDoc, name: string): Promise<void> => {
  const url = URL.createObjectURL(new Blob([await buildBabylon(engine, doc)], { type: 'application/babylon' }));
  const link = document.createElement('a');

  link.href = url;
  link.download = `${name}.babylon`;
  document.body.append(link);
  link.click();
  link.remove();

  // Revoking in the same tick cancels the download that was just started.
  setTimeout(() => URL.revokeObjectURL(url), 0);
};

/**
 * Save, as a build (devplan §3): document → graph → `.babylon` → S3 → revision.
 *
 * The bytes go straight from the tab to S3 on a URL the service signed for
 * exactly this size and type; nothing of the scene passes through a Lambda.
 * Publishing is a separate call because it is the one that makes the revision
 * the thing worlds render — an upload that fails leaves every world on the
 * revision it already had.
 */
export const publishBabylon = async (
  engine: AbstractEngine,
  doc: SceneDoc,
  worldId: string,
  sceneId: string,
  api: ApiClient,
  /** Base64 JPEG of the viewport, captured before the build so it pictures what the author saw. */
  thumbnail?: string
): Promise<SceneResource> => {
  const built = await buildBabylon(engine, doc);
  const body = new Blob([built], { type: SCENE_CONTENT_TYPE });

  const { url, revision } = await api.createSceneUploadUrl(worldId, sceneId, body.size);

  const response = await fetch(url, { method: 'PUT', headers: { 'Content-Type': SCENE_CONTENT_TYPE }, body });
  if (!response.ok) {
    throw new Error(`The scene could not be uploaded (${response.status}).`);
  }

  return api.publishScene(worldId, sceneId, { revision, ...(thumbnail ? { thumbnail } : {}) });
};
